import React, { FC, memo, useState } from 'react';
import { EmptyObject } from '@reduxjs/toolkit';
import { BrandButton } from '../../../../../../../everydoggyV4/brandButton/brandButton';
import locales from '../../../../../../../../content.json';
import { CardNumberElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toggleLoader, updatePurchaseFlow } from '../../../../../../../../reduxStore/navigation/actions';
import { useDispatch, useSelector } from 'react-redux';
import { IStripeViewModel } from '../../viewModel';
import { PaymentActionResult } from '../../types';
import { analyticsEventsTracker, EVENT_TRACKERS, GTMEvent, GTMOnboardingFunnelEvent, GTMQuizFunnelEvent } from '../../../../../../../../utils/analytics';
import { RootState } from '../../../../../../../../store';

type Props = {
  disabled?: boolean;
  email: string;
  amount?: number;
  viewModel: IStripeViewModel;
  setError?: React.Dispatch<React.SetStateAction<string>>;
};

const GetPlanButton: FC<Props> = props => {
  const { disabled, email, amount, viewModel, setError } = props;
  const stripeAmount = useSelector((state: RootState) => {
    return state.data.productList ? state.data.productList.price : '';
  });
  const stripelName = useSelector((state: RootState) => {
    return state.data.productList ? state.data.productList.product_key : '';
  });
  const productId = useSelector((state: RootState) => {
    return state.data.productList ? state.data.productList.id : '';
  });

  const [disableSubmitButton, setDisableSubmitButton] = useState(false);

  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const showLoader = payload => {
    dispatch(toggleLoader(payload));
  };

  const togglePurchaseFlow = (upselPDF: boolean) => {
    dispatch(updatePurchaseFlow({upselPDF: upselPDF}));
  };

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setDisableSubmitButton(true);
    event.stopPropagation();
    let productType: string;
    if (stripelName === 'puppy_guide') {
      productType = 'puppy';
    } else {
      productType = 'adult';
    }
    
    analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.paymentIntentionPdf(stripelName, productType, 'stripe', stripeAmount, productId), EVENT_TRACKERS.GTM);

    if (!stripe || !elements) return;

    const cardNumberElement = elements.getElement(CardNumberElement);

    stripe
      .createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          email,
        },
      })
      .then(result => {
        // Handle result.error or result.paymentMethod
        if (result.error) {
          viewModel.processPaymentError(result.error);
          setError(result.error.message);
          setDisableSubmitButton(false);
          return;
        }

        showLoader(true);

        const { id } = result.paymentMethod;
        const paymentMethodEmail: string | null = result.paymentMethod.billing_details.email;

        viewModel
          .processPaymentAction(id, productId, paymentMethodEmail || email, stripeAmount, stripelName, stripe)
          .then((result: EmptyObject) => {
            if(result) {
              analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.paymentSuccessfulPdf(stripelName, productType, 'stripe', stripeAmount, productId), EVENT_TRACKERS.GTM);
              setDisableSubmitButton(false);
              setTimeout(() => {
                showLoader(false);
                togglePurchaseFlow(true);
              }, 1000);
            }
          })
          .catch(error => {
            showLoader(false);
            setDisableSubmitButton(false);
            if (error.message && error.message.includes('Digital product purchase')) {
              setError(`Digital product purchase already exists for ${email}`);
            } else {
              setError(error.message || 'Error. Try again later');
            }
          });
      });
  };

  return (
    <BrandButton
      data-test="pay-button"
      onClick={handleSubmit}
      type="submit"
      disabled={disabled || disableSubmitButton}
      animateButton={!disabled}
    >
      <p>
        {locales[window.userLang].pdfUpselling_14}
      </p>
    </BrandButton>
  );
};

export default memo(GetPlanButton);
